import React from "react";
import { Typography, Box, Button } from "@mui/material";
import Grid from "@mui/material/Grid2";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  getCurrentBilling,
  getAccountLookup,
  getTooltipMsgs,
} from "selectors/energyAssistance.selectors";
import { InfoTooltip } from "_components";
import { getCurrentBillPDFApi } from "services/energyAssistance/energyAssistance.thunks";
import { useSelector, useDispatch } from "react-redux";
import dayjs from "dayjs";
import styles from "./index.module.css";
import { initialState } from "container/energyAssistance/store/initialState";
import { API_LOAD_STATUS, ERR_MSG } from "constants/labels";
import { alertActions } from "_store";

// OpenBalances, SecurityDepositAmountAssessedInTotal, and
// SecurityDepositOpenAmount are NOT part of getCurrentBillingApi's response.
// They come from the account-lookup API call made in EnergyAssistance.jsx
// and are passed down as props through EnergyAssistanceDetails.
const CurrentBilling = ({
  OpenBalances,
  SecurityDepositAmountAssessedInTotal,
  SecurityDepositOpenAmount,
}) => {
  const header = "Current Billing";
  const dispatch = useDispatch();
  const currentBilling = useSelector(getCurrentBilling);
  const tooltipMsgs = useSelector(getTooltipMsgs);
  const getTooltip = (title) =>
    tooltipMsgs?.Data?.find((t) => t.TooltipTitle === title)?.TooltipMsg;
  const { Data, Load, Succeeded } =
    currentBilling || initialState.currentBilling;
  const { amountDue, dueDate, pastDue, asOfDate } =
    Data || initialState.currentBilling.Data;

  const { AccountNumber } = currentBilling?.Data || "";

  const convertDate = (rawDate) => {
    return dayjs(rawDate).format("MM-DD-YYYY");
  };

  const formatCurrency = (value) =>
    value !== null && value !== undefined ? `$${Number(value).toFixed(2)}` : "-";

  const handleDownloadPDF = async () => {
    try {
      const currentBillingPDFData = await dispatch(
        getCurrentBillPDFApi({
          AccountNumber,
        })
      ).unwrap();

      if (currentBillingPDFData?.Data?.DocContent === "") {
        dispatch(
          alertActions.error({
            message: currentBillingPDFData?.Data?.SapErrors[0]?.MessageTxt1,
            header: header,
          })
        );
        return;
      }

      if (currentBillingPDFData) {
        const binary = atob(currentBillingPDFData.Data.DocContent);
        const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
        const blob = new Blob([bytes], { type: "application/pdf" });

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `CurrentBill_${convertDate(asOfDate)}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header: header,
        })
      );
    }
  };

  return (
    <Box
      className="userInformationcontainer"
      sx={{ height: "100% !important" }}
    >
      <Accordion component="div" defaultExpanded>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1-content"
          id="panel1-header"
          component="div"
        >
          <Typography component="h2" className="userInformation">
            Current Billing
            <InfoTooltip
              title="Current Billing"
              message={getTooltip("Current Billing")}
            />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {Load.status === API_LOAD_STATUS.FULFILLED && Succeeded && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                <Typography component="div" className="UserName">
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                      <Typography component="span" className="textleft">
                        Amount Due
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                      <Typography component="span" className="textright">
                        <span className="textrighttext">
                          {(amountDue && "$" + amountDue) || "-"}
                        </span>
                      </Typography>
                    </Grid>
                  </Grid>
                </Typography>

                <Typography component="div" className="UserName">
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                      <Typography component="span" className="textleft">
                        Due Date
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                      <Typography component="span" className="textright">
                        {dueDate || "-"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Typography>

                <Typography component="div" className="UserName">
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                      <Typography component="span" className="textleft">
                        Past Due
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                      <Typography component="span" className="textright">
                        {pastDue || "-"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Typography>

                <Typography component="div" className="UserName">
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                      <Typography component="span" className="textleft">
                        Open Balance
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                      <Typography component="span" className="textright">
                        {formatCurrency(OpenBalances)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Typography>

                <Typography component="div" className="UserName">
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                      <Typography component="span" className="textleft">
                        Total Security Deposit Assessed
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                      <Typography component="span" className="textright">
                        {formatCurrency(SecurityDepositAmountAssessedInTotal)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Typography>

                <Typography component="div" className="UserName">
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                      <Typography component="span" className="textleft">
                        Open Security Deposit Amount
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                      <Typography component="span" className="textright">
                        {formatCurrency(SecurityDepositOpenAmount)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Typography>

                <Typography component="div" className="UserName">
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                      <Typography component="span" className="textleft">
                        View Bill In
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                      <Button
                        variant="contained"
                        className={styles.downloadPDFButton}
                        onClick={handleDownloadPDF}
                      >
                        PDF
                      </Button>
                    </Grid>
                  </Grid>
                </Typography>
              </Grid>
            </Grid>
          )}
          {Load.status === API_LOAD_STATUS.PENDING && (
            <Typography component="p" sx={{ textAlign: "center" }}>
              Loading Current Billing...
            </Typography>
          )}
          {(Load.status === API_LOAD_STATUS.REJECTED ||
            Load.status === API_LOAD_STATUS.FULFILLED) &&
            !Succeeded && (
              <Typography component="p" sx={{ textAlign: "center" }}>
                {Load.message || "No records to display."}
              </Typography>
            )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default CurrentBilling;